import { useEffect, useRef } from "react";

const CHIME_URL = "/sounds/sos-alert.wav";

type SosAlertChimeProps = {
  activeAlertIds: number[];
  loading: boolean;
  organizationId?: number;
};

const SosAlertChime = ({ activeAlertIds, loading, organizationId }: SosAlertChimeProps) => {
  const chime = useRef<HTMLAudioElement>(null);
  const heard = useRef<{ organizationId: number, ids: Set<number> } | null>(null);
  const pendingChimeIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!organizationId) { return; }

    const removeUnlockListeners = () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };

    const unlock = () => {
      removeUnlockListeners();
      const element = chime.current;
      if (!element) { return; }

      const retryPendingChime = pendingChimeIds.current.size > 0;
      pendingChimeIds.current.clear();
      element.muted = !retryPendingChime;
      element.play().then(() => {
        if (!retryPendingChime) {
          element.pause();
          element.currentTime = 0;
        }
      }).catch(() => {}).finally(() => {
        element.muted = false;
      });
    };

    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });

    return removeUnlockListeners;
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || loading) {
      heard.current = null;
      pendingChimeIds.current.clear();
      return;
    }

    const known = heard.current?.organizationId === organizationId ? heard.current.ids : null;
    heard.current = { organizationId, ids: new Set([...(known ?? []), ...activeAlertIds]) };
    const activeAlertIdSet = new Set(activeAlertIds);
    pendingChimeIds.current = new Set(
      [...pendingChimeIds.current].filter(id => activeAlertIdSet.has(id))
    );

    const newAlertIds = known ? activeAlertIds.filter(id => !known.has(id)) : [];
    if (newAlertIds.length > 0) {
      newAlertIds.forEach(id => pendingChimeIds.current.add(id));
      chime.current?.play().then(() => {
        newAlertIds.forEach(id => pendingChimeIds.current.delete(id));
      }).catch(() => {});
    } else if (!known) {
      pendingChimeIds.current.clear();
    }
  }, [activeAlertIds, loading, organizationId]);

  return organizationId ? <audio ref={chime} src={CHIME_URL} preload="auto" /> : null;
};

export default SosAlertChime;
